import { getGraphQLClient } from '@services/graphql-client';
import { getCookie } from 'cookies-next';
import { AnonymousUser } from '@blinq-types';
import {
  CreateUserDocDocument,
  CreateUserDocMutation,
  CreateUserDocMutationVariables,
} from '@graphql/mutations/user/create-user-doc.graphql';
import { sanitizeForDb, toDBUser } from './transformers';
import firebase from 'firebase';

import { getFeatureVariant } from './server-utils/feature-flags';
import {
  UpdateUserDocDocument,
  UpdateUserDocMutation,
  UpdateUserDocMutationVariables,
} from '@graphql/mutations/user/update-user-doc.graphql';
import { analytics } from '@modules/analytics';

export const fetchGclidAndFbclid = (userId: string) => {
  const gclid = localStorage.getItem('gclid') ?? undefined; // don't set to null
  const fbclid = localStorage.getItem('fbclid') ?? undefined;

  if (gclid || fbclid) {
    analytics.identify({ user_id: userId, traits: { gclid, fbclid } });
  }

  return { gclid, fbclid };
};

export const fetchGclid = (userId: string) => {
  const gclid = localStorage.getItem('gclid');

  if (gclid && typeof analytics !== 'undefined') {
    analytics.identify({ user_id: userId, traits: { gclid: gclid } });
    return gclid;
  }

  return undefined;
};

export const cacheUser = (user: AnonymousUser) => {
  localStorage.setItem('user', JSON.stringify(user));
};

// Taken from firebase-api/user-api/createUser().
// Due to be deprecated.
export const oldCreateUser = async (user: AnonymousUser, cache = true): Promise<void> => {
  const collectionRef = firebase.firestore().collection('users');
  const docRef = collectionRef.doc(user.userId);
  // this was saved earlier in the user create flow
  // update it here just in case
  user.gclid = fetchGclid(user.userId);

  if (cache) {
    cacheUser(user);
  }

  const dbUser = toDBUser(user);

  const sanitizedDbUser = sanitizeForDb(dbUser);

  const newUser = await docRef.set(sanitizedDbUser);

  return newUser;
};

// Taken from firebase-api/user-api/updateUser().
// Due to be deprecated.
export const oldUpdateUser = async (user: AnonymousUser, cache = true): Promise<void> => {
  const collectionRef = firebase.firestore().collection('users');
  const docRef = collectionRef.doc(user.userId);

  const { gclid, fbclid } = fetchGclidAndFbclid(user.userId);

  if (gclid) {
    user.gclid = gclid;
  }
  if (fbclid) {
    user.fbclid = fbclid;
  }

  if (cache) cacheUser(user);

  const dbUser = toDBUser(user);
  const sanitizedDbUser = sanitizeForDb(dbUser);

  return await docRef.set(sanitizedDbUser, { merge: true });
};

export const createUserDoc = async (user: AnonymousUser, cache = true) => {
  const token = getCookie('token');
  const client = getGraphQLClient(token);

  const useGQL = await getFeatureVariant('schrodinger_create_update_user_doc', 'off', {
    userId: user.userId,
  });

  if (useGQL === 'on') {
    user.gclid = fetchGclid(user.userId);
    if (cache) {
      cacheUser(user);
    }

    await client.request<CreateUserDocMutation, CreateUserDocMutationVariables>(CreateUserDocDocument, {
      input: {
        data: user,
      },
    });
  } else {
    await oldCreateUser(user, cache);
  }
};

export const updateUserDoc = async (user: AnonymousUser, cache = true) => {
  const token = getCookie('token');
  const client = getGraphQLClient(token);

  const useGQL = await getFeatureVariant('schrodinger_create_update_user_doc', 'off', {
    userId: user.userId,
  });

  if (useGQL === 'on') {
    const { gclid, fbclid } = fetchGclidAndFbclid(user.userId);

    if (gclid) {
      user.gclid = gclid;
    }
    if (fbclid) {
      user.fbclid = fbclid;
    }

    if (cache) cacheUser(user);

    await client.request<UpdateUserDocMutation, UpdateUserDocMutationVariables>(UpdateUserDocDocument, {
      input: {
        data: user,
      },
    });
  } else {
    await oldUpdateUser(user, cache);
  }
};
